import React from "react";
import { useApp } from "../state";
import { explorerUrl } from "../lib/network";

/** How a field relates to the outcome the user is after.
 *
 *  "mint" and "pool" are not decoration: the Launch panel validates the two
 *  groups separately and will run a mint without any pool field set, so a
 *  badge here corresponds to a real branch in the code rather than an opinion
 *  about what looks important. */
export type Requirement = "mint" | "pool" | "optional" | "irreversible";

const REQ_LABEL: Record<Requirement, string> = {
  mint: "Required to mint",
  pool: "Required for the pool",
  optional: "Optional",
  irreversible: "Irreversible",
};

const REQ_TITLE: Record<Requirement, string> = {
  mint: "The token cannot be created without this.",
  pool: "Only needed if you are also opening a Raydium pool in this run.",
  optional: "The launch succeeds whether or not you set this.",
  irreversible: "Once this transaction confirms, nothing can undo it.",
};

export function Req({ kind }: { kind: Requirement }) {
  return (
    <span className={`req req-${kind}`} title={REQ_TITLE[kind]} data-req={kind}>
      {REQ_LABEL[kind]}
    </span>
  );
}

export function Field(props: {
  label: string;
  hint?: string;
  req?: Requirement;
  children: React.ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {props.label}
        {props.req ? <Req kind={props.req} /> : null}
      </span>
      {props.children}
      {props.hint ? <div className="hint">{props.hint}</div> : null}
    </label>
  );
}

export function Note(props: {
  kind?: "warn" | "danger" | "ok";
  children: React.ReactNode;
}) {
  return <div className={`note ${props.kind ?? ""}`}>{props.children}</div>;
}

export function Addr({ value, kind = "address" }: { value: string; kind?: "tx" | "address" }) {
  const { net } = useApp();
  const short = value.length > 16 ? `${value.slice(0, 6)}…${value.slice(-6)}` : value;
  return (
    <a href={explorerUrl(net, kind, value)} target="_blank" rel="noreferrer" title={value}>
      {short}
    </a>
  );
}

export function LogView() {
  const { log, clearLog } = useApp();
  if (!log.length) return null;
  const cls = { info: "", ok: "s", warn: "w", error: "e" } as const;
  return (
    <div className="card">
      <div className="row" style={{ justifyContent: "space-between" }}>
        <h2>Activity</h2>
        <button className="ghost" onClick={clearLog} data-testid="btn-clearlog">
          Clear
        </button>
      </div>
      <div className="log" data-testid="log">
        {log.map((l, i) => (
          <div key={i} className={cls[l.level]}>
            {new Date(l.t).toLocaleTimeString()} {l.text}
          </div>
        ))}
      </div>
    </div>
  );
}

export function RequireWallet({ children }: { children: React.ReactNode }) {
  const { wallet } = useApp();
  if (!wallet) {
    return (
      <div className="card">
        <div className="empty">Connect a wallet to use this panel.</div>
      </div>
    );
  }
  return <>{children}</>;
}

export function RequireToken({ children }: { children: React.ReactNode }) {
  const { token } = useApp();
  if (!token) {
    return (
      <div className="card">
        <div className="empty">
          No token selected. Launch one, or load an existing mint from the Dashboard.
        </div>
      </div>
    );
  }
  return <>{children}</>;
}

export function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="card" style={{ margin: 0, padding: 14 }}>
      <div style={{ color: "var(--muted)", fontSize: 11, textTransform: "uppercase", letterSpacing: ".5px" }}>
        {label}
      </div>
      <div style={{ fontSize: 18, marginTop: 6, fontFamily: "var(--mono)", wordBreak: "break-all" }}>
        {value}
      </div>
    </div>
  );
}
